import React, { FunctionComponent, useRef, useState } from 'react';
import { makeStyles, Popover } from '@material-ui/core';
import {
  CancelIcon,
  EditIcon,
  HelpIcon,
  SaveIcon,
} from 'app/assets/images/icons';
import { _t } from 'app/utils/i18n';

import styles from './PopperQuestionMark.module.scss';
import TextInput from '../Form/Inputs/TextInput';
import TextareaInput from '../Form/Inputs/TextareaInput';
import { useCustomForm } from 'app/hooks/useCustomForm';
import { schema } from './schema';
import { useMutation, useQuery } from 'react-query';
import Loader from '../Loader';
import { getHelpData, updateHelpers } from 'settings/Requests';
import { useAuth } from 'app/context/auth';
import { useLocation } from 'react-router-dom';
import { checkIsStoryBookPath } from 'app/utils/check-is-story-book-path';

interface Props {
  isLabel?: boolean;

  compId?: string;
}

const useStyles = makeStyles((theme) => ({
  popover: {
    pointerEvents: 'none',
  },
  popoverContent: {
    pointerEvents: 'auto',
  },
  paper: {
    padding: theme.spacing(0),
  },
}));

const PopperQuestionMark: FunctionComponent<Props> = ({ isLabel, compId }) => {
  const { user } = useAuth();

  const classes = useStyles();
  const [openedPopover, setOpenedPopover] = useState(false);
  const defaultValues = {
    description: '',
    url: '',
  };
  const popoverAnchor = useRef(null);

  const popoverEnter = () => {
    setOpenedPopover(true);
  };

  const popoverLeave = () => {
    setOpenedPopover(false);
  };

  const [helpFormOpen, setHelpFormOpen] = useState<boolean>(false);
  const { pathname } = useLocation();
  const moduleName = compId?.split('_', 1)?.[0];
  const moduleNameForApi =
    moduleName == 'sets'
      ? `hashtags${checkIsStoryBookPath()}sets`
      : pathname.split('/')?.[1] == 'trends' && moduleName != 'trends'
      ? `trends${checkIsStoryBookPath()}${moduleName}`
      : moduleName;
  const {
    register,
    formState: { errors },
    getValues,
    handleSubmit,
    reset,
  } = useCustomForm(schema, defaultValues);
  const values = getValues();

  const { isLoading, refetch } = useQuery(
    ['helpersData', moduleNameForApi],
    getHelpData,
    {
      staleTime: 0,
      onSuccess: (records) => {
        reset(records?.filter((rec: any) => rec?.key === compId)[0]);
      },
    }
  );

  const { mutate: submitHelpMutation } = useMutation(updateHelpers);

  const onSubmit = (value: any) => {
    submitHelpMutation({
      key: compId,
      description: value.description,
      url: value.url,
      moduleName: moduleNameForApi,
    });
    setHelpFormOpen(false);
  };
  const onCancel = () => {
    refetch();
    setHelpFormOpen(false);
  };
  if (isLoading) return <Loader />;
  return (
    <div className={styles.PopperQuestionMark}>
      <i
        ref={popoverAnchor}
        aria-owns="mouse-over-popover"
        aria-haspopup="true"
        onMouseEnter={popoverEnter}
        onMouseLeave={popoverLeave}
        className={styles.iconHolder}
        data-testid="icon-container"
      >
        <HelpIcon
          data-testid="popperQuestionMarkHelp"
          width={isLabel ? '14' : '17'}
          height={isLabel ? '14' : '17'}
        />
      </i>
      <div className="popper">
        <Popover
          id="mouse-over-popover"
          className={classes.popover}
          classes={{
            paper: classes.popoverContent,
          }}
          open={openedPopover}
          anchorEl={popoverAnchor.current}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            onMouseEnter: popoverEnter,
            onMouseLeave: popoverLeave,
          }}
          disableRestoreFocus
        >
          <div className={styles.popper} data-testid="popper__holder">
            <div className={styles.helpSubmit}>
              <p className="mb-10">{_t('basic:help')}</p>
              {user?.site_admin && (
                <div className={styles.helpControls}>
                  {helpFormOpen ? (
                    <>
                      <span
                        data-testid="popperQuestionMarkSave"
                        onClick={handleSubmit(onSubmit)}
                      >
                        <SaveIcon style={{ width: 14, height: 10 }} />
                      </span>
                      <span
                        data-testid="popperQuestionMarkCancel"
                        onClick={onCancel}
                      >
                        <CancelIcon style={{ width: 10, height: 10 }} />
                      </span>
                    </>
                  ) : (
                    <span
                      style={{ cursor: 'pointer' }}
                      onClick={() => setHelpFormOpen(true)}
                      data-testid="popperQuestionMarkEdit"
                    >
                      <EditIcon style={{ width: 14, height: 14 }} />
                    </span>
                  )}
                </div>
              )}
            </div>
            <div>
              {!helpFormOpen ? (
                <>
                  <p className={styles.description} title={values?.description}>
                    {values?.description}
                  </p>
{/*                   {values?.url?.length ? (
                    <a
                      href={values?.url}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.link}
                    >
                      {_t('basic:Visit_external_link')}
                    </a>
                  ) : (
                    ''
                  )} */}
                </>
              ) : (
                <>
                  <p className="mb-20">
                    <TextareaInput
                      placeholder={_t('basic:description')}
                      rows={4}
                      name="description"
                      register={register}
                      error={errors?.description?.message}
                    />
                  </p>
                  <p>
                    <TextInput
                      type="url"
                      name="url"
                      register={register}
                      error={errors?.url?.message}
                      placeholder={_t('basic:url')}
                    />
                  </p>
                </>
              )}
            </div>
          </div>
        </Popover>
      </div>
    </div>
  );
};

export default PopperQuestionMark;
